/**
 * Responsive animation utilities for GSAP
 * Converts pixel-based values to viewport-relative units
 */

/**
 * Convert pixel value to viewport width percentage
 * @param {number} px - Pixel value based on 1920px design width
 * @returns {string} - Viewport width value
 */
export const vw = (px) => {
  const baseWidth = 1920;
  return `${(px / baseWidth) * 100}vw`;
};

/**
 * Convert pixel value to viewport height percentage
 * @param {number} px - Pixel value based on 1080px design height
 * @returns {string} - Viewport height value
 */
export const vh = (px) => {
  const baseHeight = 1080;
  return `${(px / baseHeight) * 100}vh`;
};

/**
 * Get responsive value based on viewport size
 * @param {number} desktopValue - Value for desktop screens
 * @param {number} mobileValue - Value for mobile screens
 * @returns {number} - Calculated value based on current viewport
 */
export const getResponsiveValue = (desktopValue, mobileValue) => {
  const viewportWidth = window.innerWidth;
  const breakpoint = 768;
  
  if (viewportWidth <= breakpoint) {
    return mobileValue;
  }
  
  // Linear interpolation between mobile and desktop values
  const ratio = Math.min(viewportWidth / 1920, 1);
  return mobileValue + (desktopValue - mobileValue) * ratio;
};

/**
 * Create responsive GSAP animation values
 * @param {Object} values - Object with animation properties
 * @returns {Object} - Converted values with viewport units
 */
export const responsiveAnimation = (values) => {
  const converted = {};
  
  for (const [key, value] of Object.entries(values)) {
    if (typeof value === 'number') {
      // Convert position values to viewport units
      if (key === 'x' || key === 'left' || key === 'right') {
        converted[key] = vw(value);
      } else if (key === 'y' || key === 'top' || key === 'bottom') {
        converted[key] = vh(value);
      } else {
        // Keep other numeric values as-is (scale, rotation, etc.)
        converted[key] = value;
      }
    } else {
      converted[key] = value;
    }
  }
  
  return converted;
};

/**
 * Update ScrollTrigger for responsive behavior
 * @param {Object} config - ScrollTrigger configuration
 * @returns {Object} - Updated configuration
 */
export const responsiveScrollTrigger = (config) => {
  const viewportWidth = window.innerWidth;
  const isMobile = viewportWidth <= 768;
  
  return {
    ...config,
    // Adjust scrub speed for mobile
    scrub: isMobile ? 0.3 : (config.scrub || 0.5),
    // Adjust end position for mobile
    end: config.end || (isMobile ? "+=200%" : "+=250%"),
  };
};